import type { RoomLayout } from '../types.ts';

/**
 * Skill rule interface representing client architecture preferences/guidelines.
 */
export interface Skill {
  id: string;
  name: string;
  description: string;
  bodyContent?: string;
  content: string;
  enabled: boolean;
  filePath: string;
}

export interface SkillInput {
  name: string;
  description: string;
  content: string;
  enabled?: boolean;
}

/**
 * Action type returned by the Room Architect AI.
 * - 'CHAT': Standard conversational response advising the user.
 * - 'EXECUTE_ORCHESTRATOR': User approved layout change; returns generated prompt for Orchestrator.
 */
export type ArchitectAction = 'CHAT' | 'EXECUTE_ORCHESTRATOR';

export interface SkillSummary {
  id: string;
  name: string;
  description: string;
}

/**
 * Structured output schema from Gemini API.
 */
export interface ArchitectChatResult {
  userMessage: string;
  action: ArchitectAction;
  targetTextareaValue?: string;
  explanation?: string;
  usedSkills?: SkillSummary[];
}

/**
 * Single chat message representation.
 */
export interface ChatMessage {
  id: string;
  sender: 'user' | 'architect';
  text: string;
  timestamp: string;
  action?: ArchitectAction;
  targetTextareaValue?: string;
  modelUsed?: string;
  usedSkills?: SkillSummary[];
}

/**
 * API Request payload for /api/architect-chat
 */
export interface ArchitectChatRequest {
  message: string;
  history?: Array<{
    role: 'user' | 'model';
    text: string;
  }>;
  roomLayout: RoomLayout;
  model?: string;
  activeSkillIds?: string[];
}

/**
 * Available Gemini AI models for Room Architect chat
 */
export const AVAILABLE_ARCHITECT_MODELS = [
  { id: 'gemini-3.7-flash', name: 'Gemini 3.7 Flash (Doporučeno)', isDefault: true },
  { id: 'gemini-3.6-flash', name: 'Gemini 3.6 Flash' },
  { id: 'gemini-3.5-flash', name: 'Gemini 3.5 Flash' },
  { id: 'gemini-3.5-flash-lite', name: 'Gemini 3.5 Flash Lite' },
  { id: 'gemini-3.1-pro', name: 'Gemini 3.1 Pro' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' }
] as const;
